// ⚠️  MOBILE-FIRST. The mobile `.mobile-only` profile is the canonical view; desktop is layered on top.
// All tap targets must remain ≥44px; safe-area insets must be respected on any fixed elements.
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { listTransactions, referralStats, streakInfo, updateProfile } from "../lib/api";
import { format } from "date-fns";
import { toast } from "sonner";
import NotificationsCard from "../components/NotificationsCard";
import StreakProtectionRows from "../components/StreakProtectionRows";
import StatInfoSheet, { STAT_INFO } from "../components/StatInfoSheet";
import { shareLink } from "../lib/share";
import { readCache, writeCache } from "../lib/swr-cache";
import { Bell, Coins, Gift, GitCommitHorizontal, Palette, Share2, Shield, Target, Zap, Activity, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { buildLadder, nextTierText } from "../lib/streak-ui";

export default function Profile() {
    const { user, refresh, signOut } = useAuth();
    const navigate = useNavigate();
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(user?.name || "");
    const [showNotifications, setShowNotifications] = useState(false);
    const notifRef = useRef(null);
    const openNotifications = () => {
        setShowNotifications(true);
        setTimeout(() => notifRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    };
    const [info, setInfo] = useState(null); // tap-to-explain sheet content
    // Stale-while-revalidate side data (ledger, ladder, invite stats).
    const cacheKey = `profile_${user?.user_id || "anon"}`;
    const [cached] = useState(() => readCache(cacheKey));
    const [txns, setTxns] = useState(cached?.txns || []);
    const [streak, setStreak] = useState(cached?.streak || null);
    const [refStats, setRefStats] = useState(cached?.refStats || null);

    useEffect(() => {
        (async () => {
            try {
                const [transactions, streakData, referrals] = await Promise.all([
                    listTransactions(), streakInfo(), referralStats(),
                ]);
                setTxns(transactions.data);
                setStreak(streakData.data);
                setRefStats(referrals.data);
                writeCache(cacheKey, { txns: transactions.data, streak: streakData.data, refStats: referrals.data });
            } catch (err) {
                console.warn("[profile] profile side data failed:", err?.message || err);
            }
        })();
    }, [cacheKey]);

    const toastFor = (result) => {
        if (result === "copied") toast.success("Link copied — paste it anywhere.");
        if (result === "failed") toast.error("Couldn't share on this device.");
    };

    const inviteFriends = async () => {
        if (!refStats) return;
        const result = await shareLink({
            title: "Train with me on nerdy.fit",
            text: "My camera counts my reps now — no gym, no equipment. Join me on nerdy.fit and we both get 50 coins:",
            url: `${window.location.origin}${refStats.invite_path}`,
        });
        toastFor(result);
    };

    const shareProfile = async () => {
        if (!refStats) return;
        const result = await shareLink({
            title: "My nerdy.fit scorecard",
            text: `My training scorecard — ${user.streak_count || 0}-day streak 🔥 nerdy.fit`,
            url: `${window.location.origin}${refStats.profile_path}`,
        });
        toastFor(result);
    };

    const save = async () => {
        try {
            await updateProfile({ name });
            toast.success("Profile updated");
            await refresh();
            setEditing(false);
        } catch (e) { toast.error("Update failed"); }
    };

    if (!user) return null;
    const ladder = buildLadder(streak, user.streak_count || 0);

    return (
        <div data-testid="profile-page">
            <div className="desktop-only">
                <div className="page-head">
                    <div>
                        <div className="eyebrow">Profile</div>
                        <h1 className="page">PROFILE</h1>
                        <div className="page-sub">{"// your stats, coins, and alerts"}</div>
                    </div>
                    <button onClick={() => navigate("/goals")} className="btn btn-ghost" data-testid="profile-goals-desktop">
                        <Target size={16} /> My goals
                    </button>
                </div>

                <div className="prof-grid">
                    <div className="panel prof-card">
                        {user.picture ? (
                            <img src={user.picture} className="big-av object-cover" alt="" />
                        ) : (
                            <div className="big-av">{user.name?.[0]}</div>
                        )}
                        {!editing ? (
                            <>
                                <div className="pn" data-testid="profile-name">{user.name}</div>
                                <div className="pe">{user.email}</div>
                                <button onClick={() => setEditing(true)} className="btn btn-ghost btn-sm mt-3" data-testid="profile-edit">Edit name</button>
                            </>
                        ) : (
                            <div className="mt-3">
                                <input className="input-tactical w-full" value={name} onChange={e => setName(e.target.value)} data-testid="profile-name-input" />
                                <div className="flex gap-2 mt-2">
                                    <button onClick={save} className="btn btn-primary flex-1" data-testid="profile-save">Save</button>
                                    <button onClick={() => setEditing(false)} className="btn btn-ghost flex-1">Cancel</button>
                                </div>
                            </div>
                        )}
                        {user.is_admin && <div className="badge cyan mt-3">Admin</div>}
                    </div>

                    <div className="prof-stats">
                        <Tile kind="hero" label="STREAK" value={`${user.streak_count}d`} testid="profile-streak" />
                        <Tile label="FIT SCORE" value={user.fit_score.toFixed(0)} testid="profile-fitscore" />
                        <Tile kind="gold" label="COINS" value={user.coins.toFixed(0)} testid="profile-coins" />
                    </div>
                </div>

                <div className="mt-6">
                    <NotificationsCard />
                </div>

                <div className="mt-8">
                    <div className="sec-label">Coin history</div>
                    <div className="panel ledger">
                        {txns.length === 0 && <div className="p-6 text-zinc-500 text-sm">No transactions yet.</div>}
                        {txns.map(t => (
                            <div key={t.txn_id} className="led-row" data-testid={`txn-row-${t.txn_id}`}>
                                <div>
                                    <div className="lk">{t.reason.replace("_", " ").toUpperCase()}</div>
                                    <div className="lt">{format(new Date(t.created_at), "PP HH:mm")}</div>
                                </div>
                                <div className={`led-amt ${t.amount >= 0 ? "pos" : "neg"}`}>
                                    {t.amount >= 0 ? "+" : ""}{t.amount.toFixed(1)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mobile-only">
                <div className="you-head">
                    {user.picture ? <img src={user.picture} className="bigav object-cover" alt="" /> : <div className="bigav">{user.name?.[0]}</div>}
                    <div className="ynm">{user.name}</div>
                    <div className="yem">{user.email}</div>
                    {user.is_admin && <span className="badge cyan mt-2"><Shield size={13} />admin</span>}
                </div>

                <div className="you-grid">
                    <button type="button" className="qstat" style={{ gridColumn: "span 2" }} onClick={() => setInfo(STAT_INFO.streak)} data-testid="profile-stat-streak"><div className="k"><Activity size={12} />Streak</div><div className="v" style={{ color: "var(--accent)" }}>{user.streak_count}d</div></button>
                    <button type="button" className="qstat" onClick={() => setInfo(STAT_INFO.fit)} data-testid="profile-stat-fit"><div className="k"><Target size={12} />Fit score</div><div className="v">{user.fit_score.toFixed(0)}</div></button>
                    <button type="button" className="qstat gold" onClick={() => setInfo(STAT_INFO.coins)} data-testid="profile-stat-coins"><div className="k"><Coins size={12} />Coins</div><div className="v">{user.coins.toFixed(0)}</div></button>
                </div>

                <div className="m-sec"><span className="lab">Career ladder</span><span className="more" style={{ color: "var(--gold)" }}>{nextTierText(streak, user.streak_count || 0).split(" - ")[0]}</span></div>
                <div className="mladder">
                    {ladder.map((lv) => (
                        <div key={lv.label} className={`st ${lv.done ? "done" : ""} ${lv.current ? "cur" : ""}`}><div className="dr" /><div className="l">{lv.label}</div></div>
                    ))}
                </div>

                <StreakProtectionRows onChange={refresh} />

                <div className="m-sec"><span className="lab">Spread the word</span></div>
                <div className="setlist">
                    <button type="button" className="setrow" onClick={inviteFriends} data-testid="profile-invite">
                        <span className="sic"><Gift /></span>
                        <span className="sl">Invite friends</span>
                        <span className="sr" style={{ color: "var(--gold)" }}>
                            {refStats?.invited ? `${refStats.invited} joined - +50 each` : "+50 coins each"}
                        </span>
                    </button>
                    <button type="button" className="setrow" onClick={shareProfile} data-testid="profile-share">
                        <span className="sic"><Share2 /></span>
                        <span className="sl">Share profile</span>
                        <span className="sr">{refStats ? `@${refStats.username}` : ""}</span>
                    </button>
                </div>

                <div className="m-sec"><span className="lab">Account</span></div>
                <div className="setlist">
                    <button type="button" className="setrow" onClick={() => navigate("/goals")} data-testid="profile-goals">
                        <span className="sic"><Target /></span>
                        <span className="sl">My goals</span>
                        <span className="sr">weekly contract</span>
                    </button>
                    <button type="button" className="setrow" onClick={openNotifications} data-testid="profile-notifications">
                        <span className="sic"><Bell /></span>
                        <span className="sl">Notifications</span>
                        <span className="sr">{showNotifications ? "Open" : "Manage"}</span>
                    </button>
                    <button type="button" className="setrow" onClick={() => navigate("/history")}>
                        <span className="sic"><GitCommitHorizontal /></span>
                        <span className="sl">Workout history</span>
                        <span className="sr">History</span>
                    </button>
                    <button type="button" className="setrow" onClick={() => setInfo(STAT_INFO.coins)}>
                        <span className="sic"><Coins /></span>
                        <span className="sl">Coins</span>
                        <span className="sr">{user.coins.toFixed(0)}</span>
                    </button>
                    {user.is_admin && (
                        <button type="button" className="setrow" onClick={() => navigate("/admin")}>
                            <span className="sic"><Shield /></span>
                            <span className="sl">Admin console</span>
                            <span className="sr">sudo</span>
                        </button>
                    )}
                </div>
                {showNotifications && (
                    <div className="mt-4" ref={notifRef} style={{ scrollMarginTop: 90 }}>
                        <NotificationsCard />
                    </div>
                )}

                <div className="m-sec"><span className="lab">Appearance</span></div>
                <div className="setlist">
                    <div className="setrow">
                        <span className="sic"><Palette /></span>
                        <span className="sl">Accent</span>
                        <span className="sr" style={{ gap: 8 }}>
                            {["#34E08A", "#5BE6E1", "#A78BFA", "#F3CA5F"].map((hex) => (
                                <span key={hex} style={{ width: 22, height: 22, borderRadius: 7, background: hex, display: "inline-block" }} />
                            ))}
                        </span>
                    </div>
                    <div className="setrow">
                        <span className="sic"><Zap /></span>
                        <span className="sl">Live camera animation</span>
                        <span className="tog on"><b /></span>
                    </div>
                </div>

                <button onClick={signOut} className="btn-block mt-6" style={{ background: "transparent", border: "1px solid var(--bdr)", color: "var(--tx)" }}>
                    <LogOut size={16} /> Sign out
                </button>
                <div className="text-center text-[10px] text-[var(--tx-4)] mt-5 mono">nerdy v1.0.0 - runs in your browser</div>
            </div>

            <StatInfoSheet info={info} onClose={() => setInfo(null)} />
        </div>
    );
}

function Tile({ label, value, testid, kind = "" }) {
    return (
        <div className={`stat-tile ${kind}`} data-testid={testid}>
            <div className="lbl">
                <span className="eyebrow">{label}</span>
            </div>
            <div className="val">{value}</div>
        </div>
    );
}
